import STTypo from 'core/shared/typo';
import { TypeOptions } from 'core/shared/typo/typo.models';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { MenuItem } from './menu-item.component';

const PANEL_GAP = 8;
const CLOSE_DELAY_MS = 200;

export interface MenuSubmenuOption {
  key: string;
  label: string;
  to: string;
  testId: string;
  icon: ReactNode;
  extra?: ReactNode;
  end?: boolean;
  target?: string;
  onClick?: () => void;
}

interface MenuSubmenuProps {
  testId: string;
  label: string;
  icon: ReactNode;
  options: MenuSubmenuOption[];
  showTitle?: boolean;
  end?: boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

interface PanelPosition {
  top: number;
  left: number;
}

export const MenuSubmenu = ({ testId, label, icon, options, showTitle = true, end = false, onClick }: MenuSubmenuProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(null);

  const openPanel = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
    const triggerRect = wrapperRef.current?.firstElementChild?.getBoundingClientRect();
    if (!triggerRect) {
      return;
    }
    const siderRect = wrapperRef.current?.closest('.st-sider')?.getBoundingClientRect();
    setPanelPosition({ top: triggerRect.top, left: (siderRect?.right ?? triggerRect.right) + PANEL_GAP });
  }, []);

  const closePanel = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(() => setPanelPosition(null), CLOSE_DELAY_MS);
  }, []);

  const closePanelNow = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
    setPanelPosition(null);
  }, []);

  useEffect(() => {
    if (!panelPosition) {
      return undefined;
    }

    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePanelNow();
      }
    };

    const closeWhenFocusLeaves = (event: globalThis.FocusEvent) => {
      const nextFocused = event.relatedTarget as Node | null;
      if (nextFocused && (wrapperRef.current?.contains(nextFocused) || panelRef.current?.contains(nextFocused))) {
        return;
      }
      closePanelNow();
    };

    document.addEventListener('keydown', closeOnEscape);
    document.addEventListener('focusout', closeWhenFocusLeaves);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.removeEventListener('focusout', closeWhenFocusLeaves);
    };
  }, [panelPosition, closePanelNow]);

  useEffect(() => () => clearTimeout(closeTimeoutRef.current), []);

  const panel = panelPosition ? (
    <nav
      ref={panelRef}
      className='st-menu-submenu-panel'
      data-testid={`${testId}-panel`}
      style={{ top: panelPosition.top, left: panelPosition.left }}
      onMouseEnter={openPanel}
      onMouseLeave={closePanel}>
      {showTitle && (
        <header className='st-menu-submenu-panel__title'>
          <STTypo type={TypeOptions.H5}>{label}</STTypo>
        </header>
      )}
      <ul className='st-menu-submenu-panel__list'>
        {options.map(option => (
          <li key={option.key}>
            <NavLink
              data-testid={option.testId}
              to={option.to}
              end={option.end}
              target={option.target}
              rel={option.target === '_blank' ? 'noopener noreferrer' : undefined}
              onClick={() => {
                option.onClick?.();
                closePanelNow();
              }}
              onFocus={openPanel}
              className={({ isActive }) => `st-menu-submenu-panel__item ${isActive ? 'st-menu-submenu-panel__item--selected' : ''}`}>
              {option.icon}
              <STTypo>{option.label}</STTypo>
              {option.extra && <span className='st-menu-submenu-panel__extra'>{option.extra}</span>}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  ) : null;

  return (
    <div ref={wrapperRef} className='st-menu-submenu' onMouseEnter={openPanel} onMouseLeave={closePanel} onFocus={openPanel}>
      <MenuItem
        showTooltip={false}
        end={end}
        testId={testId}
        to={options[0].to}
        title={label}
        icon={icon}
        label={label}
        onClick={onClick}
      />
      {panel && createPortal(panel, document.body)}
    </div>
  );
};
